/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loading } from '@carbon/react';
import classNames from 'classnames';
import useToggle from 'hooks/booleanHooks';
import PropTypes from 'prop-types';
import { uniqueId } from 'lodash';
import Copier from './Copier';
import CodeViewer from './CodeViewer';
import ExpandableDisplay from './ExpandableDisplay';
import CredentialsSelector from './CredentialsSelector';
import LanguageSelector from './LanguageSelector';

function _CodeSnippet({
  generating,
  errorText,
  availableCodeLanguages = [],
  activeCodeLanguage = '',
  onChangeCodeLanguage,
  showExpander,
  content,
  highlight,
  withCredentials,
  withLanguageSelector = true,
  onChangeCredentials,
  label
}) {
  const { t: translate } = useTranslation();

  const [expanded, toggleExpanded] = useToggle(false);

  const selectedCodeLanguage = activeCodeLanguage || availableCodeLanguages[0];

  const copyRef = useRef(`code-snippet-${uniqueId()}`);
  const copyId = copyRef.current;
  const copierRef = useRef(null);

  useEffect(() => {
    // This function collapses the expanded display when the user has previously expanded the display and
    // then switches to a code snippet where there shouldn't be any expansion. This will catch this scenario
    // and automatically minimize the expansion display.
    if (!showExpander && expanded) {
      toggleExpanded();
    }
  }, [showExpander, expanded, toggleExpanded]);

  const renderError = (errorText) => <div data-testid="snippet-error">{translate('generator_error', { error: errorText })}</div>;

  const renderContent = () =>
    highlight ? (
      <CodeViewer
        data-copytarget={copyId}
        data-testid="generated-code"
        codeLanguage={selectedCodeLanguage}
        className={classNames('codeSnippetContent', { expanded })}
      >
        {content}
      </CodeViewer>
    ) : (
      <pre>
        <code
          data-copytarget={copyId}
          className={classNames(
            'boundedSection hljs codeSnippetContent',
            { js: activeCodeLanguage === 'node' },
            { language: selectedCodeLanguage !== 'node' },
            { expanded }
          )}
        >
          <div data-testid="generated-code" dangerouslySetInnerHTML={{ __html: content }} />
        </code>
      </pre>
    );

  return (
    <div className={classNames({ codeSnippetsDisplay: !showExpander })}>
      {errorText ? (
        renderError(errorText)
      ) : (
        <div className={classNames({ body: !showExpander })}>
          <div className={classNames({ codeSnippetDropdowns: withCredentials })}>
            {withCredentials && <CredentialsSelector onChange={onChangeCredentials} />}
            {withLanguageSelector && (
              <LanguageSelector
                activeCodeLanguage={activeCodeLanguage}
                availableCodeLanguages={availableCodeLanguages}
                onChangeCodeLanguage={onChangeCodeLanguage}
              />
            )}
          </div>
          {label && (
            <h4 className="codeSnippetLabel" data-testid="code-snippet-label">
              {label}
            </h4>
          )}
          {/* eslint-disable-next-line no-nested-ternary */}
          {generating ? (
            <Loading small withOverlay={false} data-testid="loading-icon" />
          ) : showExpander ? (
            <ExpandableDisplay expanded={expanded} onExpand={toggleExpanded}>
              {renderContent()}
            </ExpandableDisplay>
          ) : (
            <div data-testid="parent-generated-content" className={classNames('cds--snippet', 'cds--snippet--multi', 'cds--snippet--wraptext')}>
              <div ref={copierRef} className={classNames('cds--snippet-container')}>
                {renderContent()}
              </div>
              <Copier element={`[data-copytarget='${copyId}']`} copierRef={copierRef} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

_CodeSnippet.propTypes = {
  availableCodeLanguages: PropTypes.arrayOf(PropTypes.string),
  activeCodeLanguage: PropTypes.string,
  generating: PropTypes.bool,
  showExpander: PropTypes.bool,
  errorText: PropTypes.string,
  onChangeCodeLanguage: PropTypes.func,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  highlight: PropTypes.bool,
  withCredentials: PropTypes.bool,
  onChangeCredentials: PropTypes.func
};

const CodeSnippet = _CodeSnippet;
export default CodeSnippet;
